
import React, { useEffect } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { PartyPopper, Frown } from 'lucide-react';
import { showConfetti } from '@/utils/confetti';
import { User } from '@/context/auth/types';

interface QuizResultMessageProps {
  quizPassed: boolean;
  score: number;
  totalAvailablePoints: number;
  attemptNumber: number;
  maxAttempts: number | null;
  attemptsExhausted: boolean;
  user: User | null;
}

const QuizResultMessage: React.FC<QuizResultMessageProps> = ({
  quizPassed,
  score,
  totalAvailablePoints,
  attemptNumber,
  maxAttempts,
  attemptsExhausted,
  user
}) => {
  // Trigger confetti effect when quiz is passed
  useEffect(() => {
    if (quizPassed) {
      showConfetti();
    }
  }, [quizPassed]);

  if (quizPassed) {
    return (
      <Alert className="bg-green-50 border-green-200 relative overflow-hidden">
        <div className="flex flex-col items-center text-center py-4">
          <PartyPopper size={48} className="text-primary mb-2 animate-bounce" />
          <AlertTitle className="text-green-800 text-xl font-bold">
            {user ? `Gefeliciteerd ${user.name}!` : "Gefeliciteerd!"}
          </AlertTitle>
          <AlertDescription className="text-green-700 text-lg">
            Je hebt {score} van de {totalAvailablePoints} punten behaald.
            <div className="font-bold mt-1 text-xl animate-pulse">Perfect score behaald!</div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive" className="relative overflow-hidden">
      <div className="flex flex-col items-center text-center py-4">
        <Frown size={48} className="mb-2 animate-pulse" />
        <AlertTitle className="text-xl font-bold">Quiz niet volledig correct</AlertTitle>
        <AlertDescription>
          Je hebt {score} van de {totalAvailablePoints} punten behaald.
          <div className="font-medium mt-1">Je moet alle vragen correct beantwoorden om de quiz te voltooien.</div>
          <div className="mt-2">
            Poging {attemptNumber}
            {maxAttempts && ` van ${maxAttempts}`}.
            {attemptsExhausted && " Je hebt het maximale aantal pogingen bereikt."}
            {!attemptsExhausted && " Je kunt het opnieuw proberen."}
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
};

export default QuizResultMessage;
